import type { PathAssociation } from '@cobliteam/api-lib/src/dashboard/pathAssociation/Types';
import type { Vehicle } from '@cobliteam/common-types';
import useCameraAssociation from '@cobliteam/hooks/src/useCamera/useCameraAssociation';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { useGetPathAssociations } from '@cobliteam/hooks/src/usePathAssociation';
import { useTranslation } from '@cobliteam/i18n';
import DatePicker, { type DateRange } from '@cobliteam/ui/components/DatePicker';
import DataTable, {
  filterGetByKeys,
  filterGetParamDates,
  filterGetParamPagination,
  filterSetParamDates,
  filterSetParamFilters,
  type IFiltersDef,
  type TFilterKeys,
} from '@cobliteam/ui-business/components/DataTable';
import { differenceInDays, endOfDay, startOfDay } from '@cobliteam/utils/date';
import type { PaginationState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import getColumnsDef from './columnsDef';
import DisabledFaceIdTag from './components/DisabledFaceIdTag';

const LIMIT_IN_DAYS = 7;

const PathAssociationTable = () => {
  const { t } = useTranslation('path-association');
  const initialDate = filterGetParamDates(1, 1);
  const [selectedDate, setSelectedDate] = useState<DateRange>({
    from: initialDate.startDate,
    to: initialDate.endDate,
  });
  const [search, setSearch] = useState('');
  const [isInvalidDateRange, setIsInvalidDateRange] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationState>(filterGetParamPagination());

  const { getCameraAssociation } = useCameraAssociation();
  const { data: cameraAssociation } = getCameraAssociation();
  const isFaceIdDisabled = cameraAssociation?.associationConfig?.entireFleetAssociationConfig?.isOn === false;

  const vehicleGroupIds = useGlobalFilterStore((state) => state.vehicleGroupIds);

  const filterDate = useMemo(
    () => ({
      start: startOfDay(selectedDate.from ?? new Date()).getTime(),
      end: endOfDay(selectedDate.to ?? new Date()).getTime(),
    }),
    [selectedDate]
  );
  const filtersDef = filterGetByKeys(['MultiSelectVehicles']);

  const getFilterValue = <T,>(key: TFilterKeys, defaultValue: T): T => {
    const filter = filtersDef.find((it) => it.key === key);
    if (!filter?.value) return defaultValue;
    return filter.value;
  };

  const handleFilterChange = (filter: IFiltersDef) => {
    const filterDef = filtersDef.find((f) => f.key === filter.key) as IFiltersDef;
    const newFilter = {
      ...filterDef,
      ...filter,
    };

    filterSetParamFilters(newFilter);
  };

  const handleDateChange = (date: DateRange) => {
    const startDate = date.from ?? new Date();
    const endDate = date.to ?? new Date();

    if (differenceInDays(endDate, startDate) >= LIMIT_IN_DAYS) {
      setIsInvalidDateRange(true);
    } else {
      setIsInvalidDateRange(false);
      setSelectedDate(date);
      filterSetParamDates({
        startDate: startDate,
        endDate: endDate,
      });
    }
  };

  const wantedVehicleIds = getFilterValue<Vehicle[]>('MultiSelectVehicles', []).map((it) => it.id);

  // Fetch current period data with server-side pagination
  const { data, isLoading, isFetching, refetch } = useGetPathAssociations(
    {
      period: filterDate,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      searchQuery: search,
      wantedVehicleIds,
      wantedGroups: vehicleGroupIds,
    },
    { enabled: !isInvalidDateRange }
  );

  // Extract vehicle IDs from current page to scope previous period query
  const currentPageVehicleIds = useMemo(() => data?.content?.map((pa) => pa.vehicle.id) ?? [], [data]);

  // Calculate previous period date range (same duration, immediately before)
  const periodDurationMs = filterDate.end - filterDate.start + 1;
  const previousFilterDate = useMemo(
    () => ({
      start: filterDate.start - periodDurationMs,
      end: filterDate.end - periodDurationMs,
    }),
    [filterDate, periodDurationMs]
  );

  // Fetch previous period data filtered by vehicle IDs from current page
  const {
    data: previousData,
    isLoading: isLoadingPrevious,
    isFetching: isFetchingPrevious,
    refetch: refetchPrevious,
  } = useGetPathAssociations(
    {
      period: previousFilterDate,
      page: 0,
      size: currentPageVehicleIds.length || 1,
      wantedVehicleIds: currentPageVehicleIds,
    },
    { enabled: !isInvalidDateRange && currentPageVehicleIds.length > 0 }
  );

  // Build map of previous period data by vehicle ID for trend comparison
  const previousDataMap = useMemo(() => {
    const map = new Map<string, PathAssociation>();
    previousData?.content?.forEach((pa) => map.set(pa.vehicle.id, pa));
    return map;
  }, [previousData]);

  // Generate column definitions with previous data for trend calculation
  const columnsDef = useMemo(() => getColumnsDef({ t, previousDataMap }), [t, previousDataMap]);

  // Sync refresh across both queries
  const handleRefresh = async () => {
    await Promise.all([refetch(), refetchPrevious()]);
  };

  return (
    <DataTable
      id='path-association-table'
      columnsDef={columnsDef}
      filtersDef={filtersDef}
      manualPagination
      manualFiltering
      showColumnFilter={false}
      data={data?.content}
      totalRows={data?.totalElements}
      pageCount={data?.totalPages}
      onChangePagination={setPagination}
      onChangeFilter={handleFilterChange}
      search={search}
      onChangeSearch={setSearch}
      datePicker={
        <DatePicker
          mode='range'
          selected={selectedDate}
          onSelect={handleDateChange}
          required
          disabledDays={{ after: new Date() }}
          presets={['today', 'yesterday', 'last-7-days']}
          max={LIMIT_IN_DAYS}
          size='sm'
        />
      }
      isLoading={(isLoading && !data) || (isLoadingPrevious && !previousData)}
      isFetching={isFetching || isFetchingPrevious}
      onRefresh={handleRefresh}
      stats={isFaceIdDisabled ? () => <DisabledFaceIdTag /> : undefined}
    />
  );
};

export default PathAssociationTable;
